import { useEffect, useRef, useState } from "react";

import {
  AccountRequestError,
  accountErrorMessage,
  getProjectRevisionHistory,
  publishProjectSourceCandidate,
  selectProjectSourceCandidate,
  type ProjectRevisionHistory as RevisionHistory,
} from "../../lib/account-client";

export interface ProjectRevisionHistoryApi {
  getProjectRevisionHistory: typeof getProjectRevisionHistory;
  selectProjectSourceCandidate: typeof selectProjectSourceCandidate;
  publishProjectSourceCandidate: typeof publishProjectSourceCandidate;
}

export const projectRevisionHistoryApi: ProjectRevisionHistoryApi = {
  getProjectRevisionHistory,
  selectProjectSourceCandidate,
  publishProjectSourceCandidate,
};

export function ProjectRevisionHistory({
  workspaceId,
  projectId,
  api = projectRevisionHistoryApi,
}: {
  workspaceId: string;
  projectId: string;
  api?: ProjectRevisionHistoryApi;
}) {
  const [history, setHistory] = useState<RevisionHistory | null>(null);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [pending, setPending] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const returnRef = useRef<HTMLButtonElement>(null);

  const load = async () => {
    const next = await api.getProjectRevisionHistory(workspaceId, projectId);
    setHistory(next);
    return next;
  };

  useEffect(() => {
    let active = true;
    void api.getProjectRevisionHistory(workspaceId, projectId)
      .then((next) => { if (active) setHistory(next); })
      .catch((cause: unknown) => {
        if (active) setError(accountErrorMessage(cause, "Doorway could not load revision history."));
      });
    return () => { active = false; };
  }, [api.getProjectRevisionHistory, projectId, workspaceId]);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => { if (selectedSnapshotId) confirmRef.current?.focus(); }, [selectedSnapshotId]);

  if (!history && !error) return <p role="status">Loading revision and draft history...</p>;
  if (!history) return <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>;

  const selected = selectedSnapshotId
    ? history.drafts.find((draft) => draft.snapshotId === selectedSnapshotId) ?? null
    : null;
  const choose = async () => {
    if (!selected) return;
    setPending(true);
    setError(null);
    try {
      const next = await api.selectProjectSourceCandidate(workspaceId, projectId, {
        snapshotId: selected.snapshotId,
        expectedCandidateVersion: history.preferredCandidate?.candidateVersion ?? 0,
      });
      setHistory(next);
      setSelectedSnapshotId(null);
      setAnnouncement(selected.isSourceHead
        ? "The exact source head is now the preferred review candidate."
        : "The older snapshot is now preferred. The source head was left unchanged.");
      returnRef.current?.focus();
    } catch (cause) {
      if (cause instanceof AccountRequestError && cause.code === "SOURCE_CANDIDATE_GENERATION_CONFLICT") {
        await load().catch(() => undefined);
        setError("Revision history changed in another session. Doorway refreshed the current candidate generation.");
      } else {
        setError(accountErrorMessage(cause, "Doorway could not select this source snapshot."));
      }
      setAnnouncement("The source candidate change was not applied.");
    } finally {
      setPending(false);
    }
  };

  const publishPreferred = async () => {
    const preferred = history.preferredCandidate;
    if (!preferred || pending) return;
    setPending(true);
    setError(null);
    try {
      const next = await api.publishProjectSourceCandidate(workspaceId, projectId, {
        snapshotId: preferred.snapshotId,
        expectedCandidateVersion: preferred.candidateVersion,
      });
      setHistory(next);
      setAnnouncement(next.published.requiresApiReview
        ? "The exact source snapshot was built. Review its detected API connections and credentials before activation."
        : "The exact source snapshot is published.");
    } catch (cause) {
      if (cause instanceof AccountRequestError && (
        cause.code === "SOURCE_CANDIDATE_GENERATION_CONFLICT"
        || cause.code === "SOURCE_PUBLISH_IN_PROGRESS"
      )) {
        await load().catch(() => undefined);
        setError("The preferred source candidate changed or is already publishing. Doorway refreshed revision history.");
      } else {
        setError(accountErrorMessage(cause, "Doorway could not publish this source snapshot."));
      }
      setAnnouncement("The source snapshot was not published.");
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="review-stack" aria-labelledby={`revision-history-${projectId}`}>
      <p className="visually-hidden" role="status" aria-live="polite">{announcement}</p>
      <div>
        <p className="step-label">Revisions</p>
        <h3 id={`revision-history-${projectId}`}>Draft and deployment history</h3>
        <p>Source selection, publishing, credentials, risk approval, and incident recovery finish in this human browser review.</p>
      </div>
      {error ? <p ref={errorRef} tabIndex={-1} role="alert">{error}</p> : null}
      {history.conflict ? (
        <div role="alert">
          <strong>{history.conflict.message}</strong>
          <span>{history.conflict.nextAction}</span>
        </div>
      ) : null}
      <div className="review-stack">
        {history.drafts.length === 0 ? <p>No synced source drafts yet.</p> : history.drafts.map((draft) => (
          <article key={draft.snapshotId} aria-labelledby={`source-snapshot-${draft.snapshotId}`}>
            <h4 id={`source-snapshot-${draft.snapshotId}`}>{draft.sourceLabel}</h4>
            <p><strong>{draft.channel}</strong> · {draft.creatorOrDevice.label} · {draft.lifecycle}</p>
            <p>Analysis: {draft.analysisState} · Files: {draft.buildProvenance.fileCount} · Bytes: {draft.buildProvenance.totalBytes}</p>
            <p>Source digest <code>{draft.sourceDigest}</code></p>
            <p>{restoreLabel(draft.restoreState)}</p>
            {draft.isPreferredCandidate ? <p><strong>Preferred review candidate</strong></p> : null}
            {draft.selectable && !draft.isPreferredCandidate ? (
              <button
                ref={selectedSnapshotId === null ? returnRef : undefined}
                type="button"
                disabled={pending}
                onClick={(event) => {
                  returnRef.current = event.currentTarget;
                  setSelectedSnapshotId(draft.snapshotId);
                }}
              >Review exact snapshot</button>
            ) : null}
          </article>
        ))}
      </div>
      {history.preferredCandidate && history.permissions.canPublish ? (
        <button type="button" disabled={pending} onClick={() => void publishPreferred()}>
          {pending ? "Publishing exact snapshot..." : "Publish preferred snapshot"}
        </button>
      ) : null}
      {selected ? (
        <div role="alertdialog" aria-modal="true" aria-labelledby={`select-source-${selected.snapshotId}`}>
          <h4 id={`select-source-${selected.snapshotId}`}>Make this exact snapshot preferred?</h4>
          <p>Doorway will select digest {selected.sourceDigest}. It will not change or rewind the source head on the connected device.</p>
          {selected.isSourceHead ? null : <p><strong>This creates intentional restore divergence from the current source head.</strong></p>}
          <button ref={confirmRef} type="button" disabled={pending} onClick={() => void choose()}>
            Confirm exact snapshot
          </button>
          <button type="button" disabled={pending} onClick={() => {
            setSelectedSnapshotId(null);
            returnRef.current?.focus();
          }}>Cancel</button>
        </div>
      ) : null}
      <section aria-labelledby={`deployments-${projectId}`}>
        <h4 id={`deployments-${projectId}`}>Deployments</h4>
        {history.deployments.length === 0 ? <p>No deployments recorded.</p> : (
          <ol>{history.deployments.map((deployment) => (
            <li key={deployment.revisionId}>
              <strong>{deployment.active ? "Active" : deployment.status}</strong>
              <span> · {deployment.buildState} · {deployment.revisionId}</span>
            </li>
          ))}</ol>
        )}
      </section>
    </section>
  );
}

function restoreLabel(state: RevisionHistory["drafts"][number]["restoreState"]): string {
  return ({
    aligned: "Preferred candidate and source head are aligned.",
    available_source_head: "Available source head. Selecting it does not affect other sources.",
    would_diverge_from_source_head: "Selecting this older snapshot leaves the device source head unchanged.",
    diverged_from_source_head: "Preferred snapshot intentionally diverges from the device source head.",
  } as const)[state];
}
